import Dialog, { useDialog } from "@imminently/decisively-ui/Dialog";
import { DialogControls } from "@/ui/dialogs/DialogHeader";
import LoadingSpinner from "@imminently/decisively-ui/LoadingSpinner";
import SplitPane from "@imminently/decisively-ui/SplitPane";
import Tabs from "@imminently/decisively-ui/Tabs";
import clsx from "clsx";

import { documentService, tasksService } from "@/app/providers/services";
import { WORKSPACE_LAYER } from "@/ui/dialogs/layers";
import Breadcrumb from "@imminently/decisively-ui/Breadcrumb";
import Form from "@imminently/decisively-ui/Form";
import Icon from "@imminently/decisively-ui/Icon";
import type { Task } from "@packages/commons";
import { type FormTask, createFormTask, createNewTask } from "./taskUtils";

import { ProjectWriterGuard } from "@/auth/ComponentGuard";
import { useFullScope } from "@/common/hooks";
import { useNotification } from "@/common/notifications";
import { useCallback, useState } from "react";
import { CommentsPanel } from "./CommentsPanel";
import {
  AssigneeSelector,
  DateSelector,
  DescriptionInput,
  PrioritySelector,
  StatusSelector,
  TagsInput,
  TaskDetail,
  TitleField,
} from "./FormControls";
import styles from "./tasks.module.scss";

export const TaskModal = ({ task, close, onSaved }: { task?: Task; close: () => void; onSaved?: () => void }) => {
  const { workspace, project, release } = useFullScope();
  const notify = useNotification();
  // TODO get task by id so we have latest and can sub to updates, use enabled: false if no id
  const { action, actionName, isUpdate, query } = tasksService.useMutateResource(task?.id ? task : undefined, {});
  const { data: document, isLoading: documentLoading } = documentService.useGetOne(task?.document ?? "", {
    enabled: !!task?.document,
  });
  // task ? createFormTask(task) : createNewTask("rule")

  const [activeTab, setActiveTab] = useState<"description" | "document">("description");

  const onSubmit = (data: FormTask) => {
    const payload = {
      type: isUpdate ? undefined : data.type || "rule",
      release: isUpdate ? undefined : release.id,
      title: data.title,
      description: data.description,
      status: data.status,
      priority: data.priority,
      dueDate: data.dueDate || undefined,
      tags: data.tags ?? [],
      assignees: data.assigneesData?.map((a) => a.identity_id) ?? [],
      document: !isUpdate && task?.document ? task.document : undefined,
    };
    action(payload)
      .then((res) => {
        notify.info(`Task ${isUpdate ? "updated" : "created"}`);
        onSaved?.();
        close();
      })
      .catch((e) => {
        notify.error(`Failed to ${isUpdate ? "update" : "create"} task`);
        console.error(e);
      });
  };
  // splitPane maxHeight: "calc(100% - 40px)"
  return (
    <Dialog.Content
      aria-describedby={undefined}
      className="p-0! flex flex-col overflow-hidden"
    >
      <Form<FormTask>
        className="h-full flex flex-col min-h-0"
        defaultValues={task ? createFormTask(task) : createNewTask("rule")}
        onSubmit={onSubmit}
      >
        <Dialog.Header className={clsx(styles.header, styles.borderBottom)}>
          <Dialog.Title className="sr-only">{isUpdate ? "Edit task" : "Create task"}</Dialog.Title>
          <Breadcrumb className={styles.breadcrums}>
            {/* TODO this should actually be driven off the task if edit, scope if create */}
            <Breadcrumb.List>
              <Breadcrumb.Item>
                <Breadcrumb.Page>{workspace?.name}</Breadcrumb.Page>
              </Breadcrumb.Item>
              <Breadcrumb.Separator />
              <Breadcrumb.Item>
                <Breadcrumb.Page>{project?.name}</Breadcrumb.Page>
              </Breadcrumb.Item>
              <Breadcrumb.Separator />
              <Breadcrumb.Item>
                <Breadcrumb.Page>Release {release?.releaseNo}</Breadcrumb.Page>
              </Breadcrumb.Item>
              {task?.document ? (
                <>
                  <Breadcrumb.Separator />
                  <Breadcrumb.Item>
                    <Breadcrumb.Page>{documentLoading ? <LoadingSpinner size="sm" /> : document?.name}</Breadcrumb.Page>
                  </Breadcrumb.Item>
                </>
              ) : null}
            </Breadcrumb.List>
          </Breadcrumb>
          <DialogControls />
        </Dialog.Header>

        <Dialog.Body className="p-0! flex-1 min-h-0">
          <SplitPane
            direction="horizontal"
            gutterSize={8}
            className={clsx(styles.body)}
          >
            <SplitPane.Pane
              minSize={300}
              preferredSize={isUpdate ? "60%" : "100%"}
            >
              <div className={clsx(styles.content, styles.container)}>
                <TitleField />
                <div className={styles.details}>
                  <TaskDetail
                    label="Status"
                    icon={<Icon name="record" />}
                  >
                    <StatusSelector />
                  </TaskDetail>
                  <TaskDetail
                    label="Assignee"
                    icon={<Icon name="users" />}
                  >
                    <AssigneeSelector />
                  </TaskDetail>
                  <TaskDetail
                    label="Due Date"
                    icon={<Icon name="date-range" />}
                  >
                    <DateSelector label="Pick a date" />
                  </TaskDetail>
                  <TaskDetail
                    label="Priority"
                    icon={<Icon name="flag" />}
                  >
                    <PrioritySelector />
                  </TaskDetail>
                  <TaskDetail
                    label="Tags"
                    icon={<Icon name="label" />}
                  >
                    <TagsInput />
                  </TaskDetail>
                </div>

                {task?.document ? (
                  <>
                    <Tabs
                      value={activeTab}
                      onValueChange={(newValue) => setActiveTab(newValue as "description" | "document")}
                      style={{ marginBottom: "1rem" }}
                    >
                      <Tabs.List>
                        <Tabs.Trigger value="description">Description</Tabs.Trigger>
                        <Tabs.Trigger
                          value="document"
                          disabled
                        >
                          Document
                        </Tabs.Trigger>
                      </Tabs.List>
                    </Tabs>
                    {activeTab === "description" && <DescriptionInput />}
                    {activeTab === "document" && (documentLoading ? <LoadingSpinner size="sm" /> : null)}
                  </>
                ) : (
                  <DescriptionInput />
                )}
              </div>
            </SplitPane.Pane>
            <SplitPane.Pane
              visible={isUpdate}
              minSize={300}
              preferredSize="40%"
              className={styles.flexGrow}
            >
              {task?.id ? <CommentsPanel taskId={task.id} /> : null}
            </SplitPane.Pane>
          </SplitPane>
        </Dialog.Body>

        <ProjectWriterGuard>
          <Dialog.Actions>
            <Dialog.Actions.PrimaryButton
              label={actionName.captilized}
              type="submit"
              loading={query.isLoading}
              disabled={query.isLoading}
            />
          </Dialog.Actions>
        </ProjectWriterGuard>
      </Form>
    </Dialog.Content>
  );
};

export const useTaskModal = () => {
  const { pushDialog } = useDialog();

  return useCallback(
    (task?: Task, onSaved?: () => void) => {
      pushDialog({
        layer: WORKSPACE_LAYER,
        options: { intent: "cover" },
        component: ({ close }) => (
          <TaskModal
            task={task}
            close={close}
            onSaved={onSaved}
          />
        ),
      });
    },
    [pushDialog],
  );
};
